/**
 * Locale configuration.
 *
 * The codes are exactly `en` and `ms` — two letters, not `ms-MY`. That is not
 * an abbreviation choice: the platform database persists the same two values
 * on `identity_users.locale` behind a CHECK constraint, and
 * `Identity\Domain\Locale` in apps/api enumerates them. A locale the renderer
 * does not know produces a blank UI rather than an error, which is why both
 * ends are closed sets rather than free text.
 */

export const LOCALES = ['en', 'ms'] as const;

export type Locale = (typeof LOCALES)[number];

/**
 * English, matching `Locale::default()` on the platform API.
 *
 * PRD §19 Q8 — whether first load should default to Malay, or be chosen at
 * signup, or inferred from IP — is open and owned by Product. This is the
 * scaffold's answer, not that question's: agree with the backend, and let an
 * explicit choice override. Nothing here sniffs `navigator.language`, which
 * would make first render non-deterministic and is a caching hazard for the
 * prerendered landing site.
 */
export const DEFAULT_LOCALE: Locale = 'en';

/**
 * Namespaces, split by lifecycle rather than by screen. `landcover` holds
 * domain vocabulary that never changes; `validation` is reused by every form;
 * `errors` is reached for at the worst possible moment and should not be
 * behind a lazy chunk. `site` is marketing copy for the landing site, kept
 * apart because it is the only namespace rewritten for reasons that have
 * nothing to do with the product changing. `auth` covers the signed-out
 * screens, which are the one part of the product a visitor sees before any
 * preference of theirs is known, and `account` the signed-in screens that
 * manage it.
 */
export const NAMESPACES = [
  'common',
  'errors',
  'validation',
  'landcover',
  'site',
  'auth',
  'account',
  'projects',
  'report',
  'tools',
] as const;

export type Namespace = (typeof NAMESPACES)[number];

export const DEFAULT_NAMESPACE = 'common' satisfies Namespace;

/** Human name of a locale, written in that locale — a language picker that
 *  says "Malay" to someone who only reads Malay has failed at its one job. */
export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  ms: 'Bahasa Melayu',
};

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}
